import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { acceptInvitation, invitedEmailOf, type JoinedWorkspace } from "@/services/team.service";

type EmailMismatch = {
  invitedEmail: string;
  currentEmail: string;
};

/**
 * `/invite/:token`, the link in the invitation email. Accepting goes through the
 * `workspace` function; signed out, the visitor signs in first and comes back.
 * Signed in as an account other than the one invited, the refusal names the
 * invited address, and the page offers to switch account instead of an error.
 */
/** Said when a failure brought no sentence of its own. */
const ACCEPT_FAILED = "Could not accept the invitation. Open the link again to retry.";

export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [mismatch, setMismatch] = useState<EmailMismatch | null>(null);
  const [joined, setJoined] = useState<JoinedWorkspace | null>(null);

  useEffect(() => {
    const accept = async () => {
      // Read inside the try: a session that cannot be read is said on the card,
      // not left as a spinner that never stops.
      let currentEmail: string | undefined;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate(`/auth?returnTo=${encodeURIComponent(pathname)}`);
          return;
        }
        currentEmail = user.email;

        const result = await acceptInvitation(token!);
        setJoined(result);
        // Show the success for a moment, then open the workspace.
        setTimeout(() => navigate(`/${result.workspace_id}/agents`), 2000);
      } catch (err) {
        const invitedEmail = invitedEmailOf(err);
        if (invitedEmail) {
          setMismatch({ invitedEmail, currentEmail: currentEmail ?? "your current account" });
        } else {
          // The server's sentence, e.g. "This invitation has expired. Ask for a new one."
          setError(err instanceof Error && err.message ? err.message : ACCEPT_FAILED);
        }
      }
    };
    void accept();
  }, [token, navigate, pathname]);

  const handleSwitchAccount = async () => {
    await supabase.auth.signOut();
    navigate(`/auth?returnTo=${encodeURIComponent(pathname)}`);
  };

  if (mismatch) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-destructive" />
              <div>
                <CardTitle>Wrong account</CardTitle>
                <CardDescription>This invitation is for a different email address</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-muted rounded-lg space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Signed in as</span>
                <span className="font-medium break-all text-right">{mismatch.currentEmail}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Invitation is for</span>
                <span className="font-medium break-all text-right">{mismatch.invitedEmail}</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Log out and sign in as <span className="font-medium">{mismatch.invitedEmail}</span> to
              accept this invitation.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={handleSwitchAccount} className="w-full">
                Log out &amp; switch account
              </Button>
              <Button variant="outline" onClick={() => navigate("/agents")} className="w-full">
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-destructive" />
              <div>
                <CardTitle>Invitation Error</CardTitle>
                <CardDescription>{error}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/agents")} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Until the accept answers, or while a signed-out visitor is sent to sign in.
  if (!joined) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 animate-spin text-primary" />
              <p className="text-muted-foreground">Processing invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
            <div>
              <CardTitle>Invitation Accepted!</CardTitle>
              <CardDescription>You've successfully joined the workspace</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Workspace:</span>
              <span className="font-medium">{joined.workspace_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Your role:</span>
              <span className="font-medium capitalize">{joined.role}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground text-center">
            Redirecting you to the dashboard...
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
